import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress
} from '@mui/material';
import { BarChart, PieChart, TrendingUp, People } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const DashboardContent = () => {
  const [stats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Removed the unused error state for cleaner code based on the provided logic

  useEffect(() => {
    setLoading(false); // No API call, so immediately set loading to false
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Use sample data if API doesn't return actual stats or if there was an error
  const dashboardStats = stats || {
    totalUsers: 1248,
    revenue: 24843,
    orders: 142,
    growth: '8.2%'
  };

  // Define chart data outside the render logic for clarity
  const revenueData = [
    { name: 'Jan', revenue: 4000, profit: 2400 },
    { name: 'Feb', revenue: 3000, profit: 1398 },
    { name: 'Mar', revenue: 9800, profit: 2000 },
    { name: 'Apr', revenue: 3908, profit: 2780 },
    { name: 'May', revenue: 4800, profit: 1890 },
    { name: 'Jun', revenue: 3800, profit: 2390 },
    { name: 'Jul', revenue: 4300, profit: 3490 },
  ];

  const activityData = [
    { name: 'Mon', registrations: 40, orders: 24 },
    { name: 'Tue', registrations: 30, orders: 13 },
    { name: 'Wed', registrations: 20, orders: 18 },
    { name: 'Thu', registrations: 27, orders: 19 },
    { name: 'Fri', registrations: 18, orders: 15 },
    { name: 'Sat', registrations: 23, orders: 21 },
    { name: 'Sun', registrations: 34, orders: 28 },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      
      {/* 1. Stats Cards (Top, Wider, Full Row) */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        
        {/* Total Users - Changed to md={3} for 4-across layout */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              padding: 2, 
              display: 'flex', 
              alignItems: 'center',
              backgroundColor: '#1976d2',
              color: 'white'
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4">{dashboardStats.totalUsers.toLocaleString()}</Typography>
              <Typography variant="subtitle1">Total Users</Typography>
            </Box>
            <People fontSize="large" />
          </Paper>
        </Grid>
        
        {/* Revenue - Changed to md={3} */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              padding: 2, 
              display: 'flex', 
              alignItems: 'center',
              backgroundColor: '#4caf50',
              color: 'white'
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4">${dashboardStats.revenue.toLocaleString()}</Typography>
              <Typography variant="subtitle1">Revenue</Typography>
            </Box>
            <TrendingUp fontSize="large" />
          </Paper>
        </Grid>
        
        {/* Orders - Changed to md={3} */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              padding: 2, 
              display: 'flex', 
              alignItems: 'center',
              backgroundColor: '#ff9800',
              color: 'white'
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4">{dashboardStats.orders.toLocaleString()}</Typography>
              <Typography variant="subtitle1">Orders</Typography>
            </Box>
            <BarChart fontSize="large" />
          </Paper>
        </Grid>
        
        {/* Growth - Changed to md={3} */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              padding: 2, 
              display: 'flex', 
              alignItems: 'center',
              backgroundColor: '#f44336',
              color: 'white'
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4">{dashboardStats.growth}</Typography>
              <Typography variant="subtitle1">Growth</Typography>
            </Box>
            <PieChart fontSize="large" />
          </Paper>
        </Grid>
      </Grid>

      {/* 2. Revenue Overview Chart (Middle, Full Width) */}
      <Grid container spacing={3} sx={{ mb: 20 }}>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ width: '100%', height: 350 }}> 
          <Card>
            <CardHeader title="Revenue Overview" />
            <CardContent>
              <Box sx={{ width: '100%', height: 350, minHeight: 0 }}> 
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={revenueData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" stroke="#1976d2" fill="#1976d2" name="Revenue" />
                    <Area type="monotone" dataKey="profit" stroke="#4caf50" fill="#4caf50" name="Profit" />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
        
      {/* 3. Recent Activity Chart (Bottom, Full Width) */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ width: '100%', height: 350 }}> 
          <Card>
            <CardHeader title="Recent Activity" />
            <CardContent>
              <Box sx={{ width: '100%', height: 350, minHeight: 0 }}> 
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={activityData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="registrations" stroke="#1976d2" name="User Registrations" />
                    <Line type="monotone" dataKey="orders" stroke="#ff9800" name="Orders" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
};

export default DashboardContent;